import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import ticketLifecycleApi, {
  HaloStatus,
  HaloTicketType,
  LookbackImpact,
  StaleTicketConfig,
  StaleTicketConfigInput,
} from "../../apis/ticketLifecycleApi";

/** Format a YYYY-MM-DD cutoff as a local "Mon D, YYYY" without UTC-shift. */
const formatCutoff = (iso: string): string => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

/** Bold field label with a small (?) accent that reveals the help text on hover.
 *  Optional right-aligned `action` (e.g. a Select all/none toggle) renders just
 *  left of the help icon. */
const LabelWithHelp: React.FC<{ label: string; help: string; action?: React.ReactNode }> = ({
  label,
  help,
  action,
}) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
    <Typography variant="body2" sx={{ fontWeight: 700 }}>
      {label}
    </Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {action}
      <Tooltip title={help} arrow>
        <HelpOutlineIcon sx={{ fontSize: 15, color: "secondary.main", cursor: "help" }} />
      </Tooltip>
    </Box>
  </Box>
);

/** Right-aligned toggle: "Select all" when nothing is selected, "Select None"
 *  when at least one item is selected. */
const SelectAllToggle: React.FC<{ anySelected: boolean; onToggle: () => void }> = ({ anySelected, onToggle }) => (
  <Button
    size="small"
    variant="text"
    color="secondary"
    onClick={onToggle}
    startIcon={
      anySelected ? (
        <IndeterminateCheckBoxIcon fontSize="small" />
      ) : (
        <CheckBoxOutlineBlankIcon fontSize="small" />
      )
    }
    sx={{ minWidth: 0, py: 0, px: 0.5, textTransform: "none", fontWeight: 600 }}
  >
    {anySelected ? "Select None" : "Select all"}
  </Button>
);

interface StaleTicketConfigFormProps {
  initialConfig: StaleTicketConfig | null;
  onSaved: (cfg: StaleTicketConfig) => void;
  onCancel?: () => void;
}

/**
 * Remove a leading-zero run from a number-input value ("05" -> "5") so editing
 * 7 -> 5 doesn't render "05". Empty stays empty (allowed transiently; fails the
 * >=1 check on save). The input is type="number", so non-digits are already blocked.
 */
const stripLeadingZeros = (v: string): string => v.replace(/^0+(?=\d)/, "");

const StaleTicketConfigForm: React.FC<StaleTicketConfigFormProps> = ({
  initialConfig,
  onSaved,
  onCancel,
}) => {
  // Backed as strings so the user can clear the field and type freely (no
  // leading-zero coercion from a number-typed controlled input). Parse to int
  // on save/validation; empty string is transiently valid during editing.
  const [staleThresholdStr, setStaleThresholdStr] = useState<string>(
    String(initialConfig?.stale_threshold_days ?? 5)
  );
  // Whether to LIMIT how far back to onboard tickets. Off (default) = unlimited,
  // which is stored as max_lookback_days = 0. On = onboard only tickets created
  // within `maxLookbackStr` days (must be >= 1). The stored 0 = "no limit"
  // semantics are unchanged; the checkbox just makes that explicit for users.
  const [lookbackLimited, setLookbackLimited] = useState<boolean>(
    (initialConfig?.max_lookback_days ?? 0) > 0
  );
  // Keep a sensible positive default so ticking the checkbox pre-fills a usable
  // value rather than an invalid "0"/blank (0 is only ever submitted via the
  // unchecked checkbox, never typed here).
  const [maxLookbackStr, setMaxLookbackStr] = useState<string>(
    String(initialConfig?.max_lookback_days && initialConfig.max_lookback_days > 0
      ? initialConfig.max_lookback_days
      : 180)
  );
  // open_status_ids holds numeric Halo status IDs (as strings). The multi-select
  // stores ids (value = status.id) and shows names (label = status.name).
  const [selectedStatusIds, setSelectedStatusIds] = useState<string[]>(
    initialConfig?.open_status_ids ?? []
  );
  // included_ticket_types holds Halo ticket-type NAMES (not ids). The multi-select
  // stores/submits names directly (label == value), so no id-normalization is needed.
  // INCLUSION list: checked = this type is shown/tracked; empty = nothing shown.
  const [selectedIncludedTypes, setSelectedIncludedTypes] = useState<string[]>(
    initialConfig?.included_ticket_types ?? []
  );

  const [statuses, setStatuses] = useState<HaloStatus[]>([]);
  const [statusesLoading, setStatusesLoading] = useState(true);
  const [statusesError, setStatusesError] = useState(false);
  const [ticketTypes, setTicketTypes] = useState<HaloTicketType[]>([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lookbackImpact, setLookbackImpact] = useState<LookbackImpact | null>(null);
  const [impactLoading, setImpactLoading] = useState(false);

  const fetchStatuses = useCallback(async () => {
    setStatusesLoading(true);
    setStatusesError(false);
    try {
      const result = await ticketLifecycleApi.getHaloStatuses();
      if (result.length === 0) {
        setStatusesError(true);
        setStatuses([]);
      } else {
        setStatuses(result);
        setStatusesError(false);
      }
    } catch {
      setStatusesError(true);
      setStatuses([]);
    } finally {
      setStatusesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  // Halo ticket types for the "Ticket Types" inclusion selector. A load failure
  // surfaces an inline retry but never blocks Save. Uses the cached variant so
  // repeated opens are instant and share one in-flight request with any other caller.
  const fetchTicketTypes = useCallback(async () => {
    setTypesLoading(true);
    setTypesError(false);
    try {
      const result = await ticketLifecycleApi.getHaloTicketTypesCached();
      setTicketTypes(result);
      setTypesError(result.length === 0);
    } catch {
      setTypesError(true);
      setTicketTypes([]);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTicketTypes();
  }, [fetchTicketTypes]);

  const toggleIncludedType = (name: string) => {
    setSelectedIncludedTypes((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name]
    );
  };

  // Normalize the selection to status IDS once the Halo status list is known.
  // A saved config may still hold legacy status NAMES (from the pre-id-revert
  // schema); seeding selectedStatusIds from that would leave names in the array
  // (invisible in the id-based checkboxes) and any newly-toggled id would be
  // APPENDED, persisting "New,In Progress,1". Here we map each stored entry to a
  // valid id (id kept as-is; a name resolved to its id) and drop unknowns, so the
  // array only ever contains ids.
  useEffect(() => {
    if (statuses.length === 0) return;
    const idSet = new Set(statuses.map((s) => String(s.id)));
    const nameToId = new Map(
      statuses.map((s) => [String(s.name).trim().toLowerCase(), String(s.id)])
    );
    setSelectedStatusIds((prev) => {
      const out: string[] = [];
      const seen = new Set<string>();
      for (const entry of prev) {
        const e = String(entry).trim();
        const id = idSet.has(e) ? e : nameToId.get(e.toLowerCase()) ?? null;
        if (id != null && !seen.has(id)) {
          seen.add(id);
          out.push(id);
        }
      }
      // Avoid a needless state update (and re-render loop) when already normalized.
      if (out.length === prev.length && out.every((v, i) => v === prev[i])) return prev;
      return out;
    });
  }, [statuses]);

  const toggleStatus = (id: string) => {
    setSelectedStatusIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Parsed values (NaN when the field is empty or non-numeric).
  const staleThresholdVal = parseInt(staleThresholdStr, 10);
  const maxLookbackVal = parseInt(maxLookbackStr, 10);

  // The value we actually submit: 0 (unlimited) when the checkbox is off, else the
  // typed number. Single source of truth for validation, dirty-check, and save.
  const effectiveMaxLookback = lookbackLimited ? maxLookbackVal : 0;

  // stale_threshold must be ≥ 1; when the lookback limit is ON the value must be ≥ 1
  // (0 = unlimited is expressed by turning the checkbox OFF, not by typing 0).
  const isFormValid =
    !isNaN(staleThresholdVal) && staleThresholdVal >= 1 &&
    (!lookbackLimited || (!isNaN(maxLookbackVal) && maxLookbackVal >= 1));

  // Created_at cutoff for the current lookback (client mirror of the server), or
  // null when unlimited / invalid. Drives the reactive "tracks since" line.
  const cutoffDateStr =
    lookbackLimited && !isNaN(maxLookbackVal) && maxLookbackVal > 0
      ? new Date(Date.now() - maxLookbackVal * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
      : null;

  // Show inline field errors only when the field has content that is out of range
  // (empty is silently invalid — blocks save without an in-field error message).
  const staleFieldError =
    staleThresholdStr !== "" && (!isNaN(staleThresholdVal) ? staleThresholdVal < 1 : true);
  const lookbackFieldError =
    lookbackLimited && maxLookbackStr !== "" && (!isNaN(maxLookbackVal) ? maxLookbackVal < 1 : true);

  // Debounced "how many open tickets does this cutoff exclude?" preview. 0/invalid
  // needs no server call; on error we clear the impact so the warning just hides.
  useEffect(() => {
    // Unlimited (checkbox off) → no server call, nothing is excluded.
    if (!lookbackLimited) {
      setLookbackImpact({ unlimited: true, cutoff_date: null, excluded_open_count: 0 });
      return;
    }
    if (isNaN(maxLookbackVal) || maxLookbackVal < 1) {
      setLookbackImpact(null);
      return;
    }
    let cancelled = false;
    setImpactLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await ticketLifecycleApi.getLookbackImpact({
          max_lookback_days: maxLookbackVal,
          open_status_ids: selectedStatusIds,
        });
        if (!cancelled) setLookbackImpact(res);
      } catch {
        if (!cancelled) setLookbackImpact(null);
      } finally {
        if (!cancelled) setImpactLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lookbackLimited, maxLookbackVal, selectedStatusIds]);

  // Dirty check: has anything changed relative to the saved config?
  // When there is no existing config (first-time onboarding), the form is
  // always considered dirty so Save is enabled whenever it is otherwise valid.
  const isDirty = (() => {
    if (!initialConfig) return true;
    if (staleThresholdVal !== initialConfig.stale_threshold_days) return true;
    if (effectiveMaxLookback !== initialConfig.max_lookback_days) return true;
    // Order-independent set comparison for status IDs.
    const initSet = new Set(initialConfig.open_status_ids);
    const currSet = new Set(selectedStatusIds);
    if (initSet.size !== currSet.size) return true;
    if (selectedStatusIds.some((id) => !initSet.has(id))) return true;
    // Order-independent set comparison for included ticket-type names.
    const initTypeSet = new Set(initialConfig.included_ticket_types);
    if (initTypeSet.size !== selectedIncludedTypes.length) return true;
    if (selectedIncludedTypes.some((name) => !initTypeSet.has(name))) return true;
    return false;
  })();

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const idsToSubmit = statusesError ? (initialConfig?.open_status_ids ?? []) : selectedStatusIds;
      const values: StaleTicketConfigInput = {
        stale_threshold_days: staleThresholdVal,
        max_lookback_days: effectiveMaxLookback,
        open_status_ids: idsToSubmit,
        included_ticket_types: selectedIncludedTypes,
        // The UI no longer exposes a disable control — saving always keeps the
        // feature enabled. (The server retains the column for ops/support use.)
        enabled: true,
      };
      const saved = await ticketLifecycleApi.saveConfig(values);
      onSaved(saved);
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled =
    saving ||
    !isFormValid ||
    (statusesError && initialConfig === null) ||
    (initialConfig !== null && !isDirty);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, p: 4 }}>
      <Box>
        <Typography variant="h5" sx={{ color: "secondary.main", fontWeight: 700 }}>
          Ticket Lifecycle Configuration
        </Typography>
      </Box>

      <Box>
        <LabelWithHelp
          label="Attention threshold (days)"
          help="A ticket with no activity for this many days is flagged as needing attention."
        />
        <TextField
          type="number"
          inputProps={{ inputMode: "numeric", min: 1 }}
          value={staleThresholdStr}
          onChange={(e) => setStaleThresholdStr(stripLeadingZeros(e.target.value))}
          error={staleFieldError}
          helperText={staleFieldError ? "Must be at least 1" : undefined}
          fullWidth
        />
      </Box>

      <Box>
        <LabelWithHelp
          label="Limit how far back to onboard tickets"
          help="By default Theo tracks all open tickets, however old. Turn this on to only onboard tickets created within a set number of days."
        />
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              color="secondary"
              checked={lookbackLimited}
              onChange={(e) => setLookbackLimited(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2">
              Limit by ticket creation date
            </Typography>
          }
        />
        {lookbackLimited && (
          <TextField
            type="number"
            label="Max lookback (days)"
            inputProps={{ inputMode: "numeric", min: 1 }}
            value={maxLookbackStr}
            onChange={(e) => setMaxLookbackStr(stripLeadingZeros(e.target.value))}
            error={lookbackFieldError}
            helperText={lookbackFieldError ? "Must be at least 1" : undefined}
            fullWidth
            sx={{ mt: 1 }}
          />
        )}
        <Box sx={{ mt: 0.5 }}>
          {!lookbackLimited ? (
              <Typography variant="caption" color="text.secondary">
                Tracks all open tickets — no age limit.
              </Typography>
            ) : !lookbackFieldError && maxLookbackStr !== "" && cutoffDateStr ? (
              <>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  Tracks tickets created on or after {formatCutoff(cutoffDateStr)}.
                </Typography>
                {impactLoading ? (
                  <Typography variant="caption" color="text.secondary">
                    Checking excluded tickets…
                  </Typography>
                ) : lookbackImpact &&
                  lookbackImpact.excluded_open_count != null &&
                  lookbackImpact.excluded_open_count > 0 ? (
                  <Typography variant="caption" color="warning.main" sx={{ display: "block" }}>
                    ⚠ {lookbackImpact.excluded_open_count} open{" "}
                    {lookbackImpact.excluded_open_count === 1 ? "ticket" : "tickets"} created before this
                    cutoff {lookbackImpact.excluded_open_count === 1 ? "is" : "are"} not tracked — turn off
                    the limit to include all.
                  </Typography>
                ) : null}
              </>
            ) : null}
        </Box>
      </Box>

      {statusesLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading Halo statuses…</Typography>
        </Box>
      ) : statusesError ? (
        <Box>
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={fetchStatuses}>
                Retry
              </Button>
            }
          >
            Couldn't load Halo statuses — check that the Halo integration is connected, then retry
          </Alert>
          {initialConfig !== null && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Using previously saved statuses: {initialConfig.open_status_ids.join(", ") || "none"}
            </Typography>
          )}
        </Box>
      ) : (
        <FormControl component="fieldset" fullWidth>
          <LabelWithHelp
            label="Open ticket statuses"
            help="Tickets in these statuses are treated as open when detecting tickets that need attention."
            action={
              <SelectAllToggle
                anySelected={selectedStatusIds.length > 0}
                onToggle={() =>
                  setSelectedStatusIds(
                    selectedStatusIds.length > 0 ? [] : statuses.map((s) => String(s.id))
                  )
                }
              />
            }
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", columnGap: 2 }}>
            {statuses.map((status) => (
              <FormControlLabel
                key={status.id}
                control={
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={selectedStatusIds.includes(String(status.id))}
                    onChange={() => toggleStatus(String(status.id))}
                  />
                }
                label={<Typography variant="body2">{status.name}</Typography>}
              />
            ))}
          </Box>
        </FormControl>
      )}

      {/* Ticket Types — inclusion list (only selected types appear in the
          overviews). A types-load failure surfaces an inline retry but must NOT
          block Save, so this block never gates the Save button. */}
      {typesLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading Halo ticket types…</Typography>
        </Box>
      ) : typesError ? (
        <Box>
          <LabelWithHelp
            label="Ticket Types"
            help="Only tickets of the selected types are shown. With none selected, no tickets are shown."
          />
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={fetchTicketTypes}>
                Retry
              </Button>
            }
          >
            Couldn't load Halo ticket types — you can still save; included types stay unchanged
          </Alert>
        </Box>
      ) : (
        <FormControl component="fieldset" fullWidth>
          <LabelWithHelp
            label="Ticket Types"
            help="Only tickets of the selected types are shown. With none selected, no tickets are shown."
            action={
              <SelectAllToggle
                anySelected={selectedIncludedTypes.length > 0}
                onToggle={() =>
                  setSelectedIncludedTypes(
                    selectedIncludedTypes.length > 0 ? [] : ticketTypes.map((t) => t.name)
                  )
                }
              />
            }
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", columnGap: 2 }}>
            {ticketTypes.map((type) => (
              <FormControlLabel
                key={type.id}
                control={
                  <Checkbox
                    size="small"
                    color="secondary"
                    checked={selectedIncludedTypes.includes(type.name)}
                    onChange={() => toggleIncludedType(type.name)}
                  />
                }
                label={<Typography variant="body2">{type.name}</Typography>}
              />
            ))}
          </Box>
        </FormControl>
      )}

      {saveError && (
        <Alert severity="error">{saveError}</Alert>
      )}

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSave}
          disabled={isSaveDisabled}
          sx={{ bgcolor: "secondary.dark", "&:hover": { bgcolor: "#403AC0" } }}
        >
          {saving ? <CircularProgress size={20} /> : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default StaleTicketConfigForm;
